/**
 * Parse a steps-target text field into the value stored on a habit.
 *
 * `undefined` means "not step-driven" — the habit behaves exactly as it always
 * has. That is why `0` is rejected rather than accepted: a zero target would
 * be met by every user on every day, silently completing the habit forever.
 *
 * Only Western digits are accepted, matching the app-wide numeral rule.
 *
 * Kept in sync with `mobile/src/lib/health/stepsTarget.ts` so a target typed in
 * the web manager and one typed on the phone are validated identically.
 */
export function parseStepsTarget(input: string): number | undefined {
  const trimmed = input.trim();
  if (trimmed.length === 0) return undefined;
  if (!/^\d+$/.test(trimmed)) return undefined;

  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value <= 0) return undefined;
  return value;
}

/**
 * Apply a steps-target text field's next value onto an already-existing habit
 * object (e.g. a competition habit being edited), returning a new object.
 *
 * The whole point of this helper is what it does when `text` doesn't parse
 * (empty, `0`, negative, non-numeric, Arabic-Indic digits — see
 * `parseStepsTarget`): the returned object has **no** `stepsTarget` key at
 * all, not a `stepsTarget: undefined` key. Firestore rejects a literal
 * `undefined` field value, and callers that spread a stale habit object
 * (`{ ...habit, category: c }`) would otherwise carry a previously-saved
 * target forward forever with nothing to clear it. Every other field on the
 * input object is preserved untouched.
 */
export function applyStepsTarget<T extends { stepsTarget?: number }>(habit: T, text: string): T {
  const target = parseStepsTarget(text);
  if (target === undefined) {
    const { stepsTarget, ...rest } = habit;
    return rest as T;
  }
  return { ...habit, stepsTarget: target };
}
